import * as React from 'react'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import IconButton from '@mui/joy/IconButton'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'

const ZOOM_STEP = 0.25
const MIN_ZOOM = 0.25
const MAX_ZOOM = 4

interface DocumentViewerProps {
    /** A short-lived link to the document; null when it could not be made (the source object may be gone). */
    url: string | null
    filename: string | null
    mimeType: string | null
}

/**
 * The whole source document, big enough to read. An image is zoomed here (in, out, back to fit, scroll to move); a PDF is shown in the
 * browser's own viewer with its toolbar on, which zooms, searches and pages. Either opens at full size in a new tab.
 */
export default function DocumentViewer({ url, filename, mimeType }: DocumentViewerProps) {
    const [zoom, setZoom] = React.useState(1)

    if (!url) {
        return (
            <Sheet
                variant="outlined"
                sx={{ borderRadius: 'md', p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: 'text.tertiary' }}
            >
                <InsertDriveFileOutlinedIcon />
                <Typography level="body-sm" textAlign="center">
                    The document cannot be shown: its link could not be made (the stored file may no longer exist).
                </Typography>
            </Sheet>
        )
    }

    const isImage = Boolean(mimeType?.startsWith('image/')) || /\.(png|jpe?g)$/i.test(filename ?? '')
    const zoomBy = (delta: number) => setZoom((z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round((z + delta) * 100) / 100)))

    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%', minHeight: 420 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                {isImage ? (
                    <>
                        <IconButton size="sm" variant="plain" aria-label="Zoom out" disabled={zoom <= MIN_ZOOM} onClick={() => zoomBy(-ZOOM_STEP)}>
                            <ZoomOutIcon />
                        </IconButton>
                        <Typography level="body-sm" sx={{ minWidth: 44, textAlign: 'center' }}>
                            {Math.round(zoom * 100)}%
                        </Typography>
                        <IconButton size="sm" variant="plain" aria-label="Zoom in" disabled={zoom >= MAX_ZOOM} onClick={() => zoomBy(ZOOM_STEP)}>
                            <ZoomInIcon />
                        </IconButton>
                        <Button size="sm" variant="plain" disabled={zoom === 1} onClick={() => setZoom(1)}>
                            Fit
                        </Button>
                    </>
                ) : (
                    <Typography level="body-xs" sx={{ px: 0.5, color: 'text.tertiary' }}>
                        Zoom, search and page with the viewer&apos;s own toolbar.
                    </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Button component="a" href={url} target="_blank" rel="noreferrer" size="sm" variant="plain" startDecorator={<OpenInNewIcon />}>
                    Open original
                </Button>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', bgcolor: 'background.level1' }}>
                {isImage ? (
                    <Box component="img" src={url} alt={filename ?? 'document'} sx={{ display: 'block', width: `${zoom * 100}%`, maxWidth: 'none' }} />
                ) : (
                    <iframe src={`${url}#toolbar=1&navpanes=0&view=FitH`} title={filename ?? 'document'} style={{ width: '100%', height: '100%', border: 0 }} />
                )}
            </Box>
        </Sheet>
    )
}
